
import React from 'react';
import { useTranslation } from 'react-i18next';
import ShinyText from './ShinyText';
import { SEO } from './SEO';
import {
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Translate as TranslateIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  Shield,
  Users,
  MenuBook,
  Globe,
  TrendingUp,
  EmojiEvents,
  AccountBalance as AccountBalanceIcon,
  Policy as PolicyIcon
} from '@mui/icons-material';

const SarvalokGovernmentBenefits: React.FC = () => {
  const { t } = useTranslation(['gov', 'seo']);
  return (
    <div className="bg-white text-black">
      <SEO
        title={t('seo:gov.title')}
        description={t('seo:gov.description')}
        url="/for-government"
      />
      {/* Academic Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-8 lg:px-20 py-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 -right-24 z-0 h-72 w-72 rounded-full bg-gradient-to-br from-orange-300/35 via-amber-300/25 to-blue-300/20 blur-3xl dark:from-orange-400/15 dark:via-amber-400/10 dark:to-blue-400/10" />
          <div className="absolute -bottom-28 -left-28 z-0 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-300/35 via-indigo-300/25 to-purple-300/20 blur-3xl dark:from-blue-400/15 dark:via-indigo-400/10 dark:to-purple-400/10" />
          <img
            src="/decor/sphere3.png"
            alt=""
            className="absolute -top-14 -right-14 z-10 hidden md:block h-auto w-auto max-w-[240px] opacity-90 drop-shadow-2xl"
          />
        </div>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
            <Shield className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            <ShinyText 
              text={t('gov.header_title_line1')} 
              speed={3} 
              className="text-gray-900" 
            />
            <span className="block text-blue-600">
              <ShinyText 
                text={t('gov.header_title_line2')} 
                speed={3} 
              />
            </span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
            {t('gov.header_description')}
          </p>
        </div>
      </div>

      <div className="px-8 lg:px-20 py-16">

        {/* How Sarvalok Serves Government */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('gov.serve_government_title')}</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Enhancing Education Systems */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <SchoolIcon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('gov.enhancing_education_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('gov.enhancing_education_desc')}
              </p>
            </div>

            {/* Supporting Cultural Ministries */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <AccountBalanceIcon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('gov.supporting_cultural_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('gov.supporting_cultural_desc')}
              </p>
            </div>

            {/* Informing Policy Decisions */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <PolicyIcon className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('gov.informing_policy_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('gov.informing_policy_desc')}
              </p>
            </div>

            {/* Promoting Cultural Literacy */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                  <LightbulbIcon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('gov.promoting_cultural_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('gov.promoting_cultural_desc')}
              </p>
            </div>

            {/* Multilingual Accessibility */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="flex items-center mb-6">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                  <TranslateIcon className="w-6 h-6 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{t('gov.multilingual_accessibility_title')}</h3>
              </div>
              <p className="text-gray-600 leading-relaxed">
                {t('gov.multilingual_accessibility_desc')}
              </p>
            </div>
          </div>
        </div>
        {/* The Sarvalok Advantage */}
        <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-2xl p-12 mb-16 text-white">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <EmojiEvents className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4">{t('gov.advantage_title')}</h2>
            <div className="w-24 h-1 bg-white/30 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <h4 className="text-xl font-semibold mb-3 text-blue-200">{t('gov.unmatched_accuracy')}</h4>
              <p className="text-gray-200 leading-relaxed">
                {t('gov.unmatched_accuracy_desc')}
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <h4 className="text-xl font-semibold mb-3 text-green-200">{t('gov.heritage_ai_insights')}</h4>
              <p className="text-gray-200 leading-relaxed">
                {t('gov.heritage_ai_insights_desc')}
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <h4 className="text-xl font-semibold mb-3 text-purple-200">{t('gov.direct_source_exploration')}</h4>
              <p className="text-gray-200 leading-relaxed">
                {t('gov.direct_source_exploration_desc')}
              </p>
            </div>

            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <h4 className="text-xl font-semibold mb-3 text-orange-200">{t('gov.efficiency_boost')}</h4>
              <p className="text-gray-200 leading-relaxed">
                {t('gov.efficiency_boost_desc')}
              </p>
            </div>
          </div>

          <div className="text-center mt-8">
            <div className="bg-white/5 rounded-xl p-6 backdrop-blur-sm">
              <h4 className="text-xl font-semibold mb-3 text-yellow-200">The Only Platform of Its Kind</h4>
              <p className="text-gray-200 leading-relaxed">
                Sarvalok stands alone in providing AI-driven access to ancient wisdom, verified, accurate, and deeply rooted in history, philosophy, and cultural studies. It is the only platform that combines heritage-driven content with cutting-edge AI technology for government bodies.
              </p>
            </div>
          </div>
        </div>

        {/* Partnership Section */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-12 mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('gov.government_partnership')}</h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
              {t('gov.government_partnership_desc')}
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-600 p-12 text-center">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{t('gov.contact_us')}</h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-semibold">📞</span>
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('gov.phone_label')}</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">{t('gov.phone_number').trim().startsWith('+') ? t('gov.phone_number') : `+91 ${t('gov.phone_number')}`}</p>
              </div>
            </div>

            <a href="https://wa.me/918125831103?text=Hello%2C%20I%20have%20a%20question%20about%20Sarvalok.%20Can%20you%20please%20help%20me%3F" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center space-x-3 text-green-600 hover:text-green-700 transition-colors duration-200">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <WhatsAppIcon className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">{t('gov.whatsapp_label')}</p>
                <p className="text-lg font-semibold dark:text-white">{t('gov.start_conversation')}</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SarvalokGovernmentBenefits;
